<template>
	<div class="commitment-row" :data-id="commitment.id">
		<div :class="wrapperClasses" @click="onCommitmentTextClicked">
			<div class="commitment-row__details-and-context-column">
				<div class="commitment-row__details-row">
					<div class="commitment-row__completion-column">
						<TeamingCheckbox
							:name="`completion-${ commitment.id }`"
							value="yes"
							:is-checked="commitment.isCompleted"
							:is-disabled="shouldDisableCheckbox"
							@change="onCompletionChange"
						/>
					</div>
					<div
						class="commitment-row__details-cell"
					>
						<div class="commitment-row__content-column">
							<FontAwesomeIcon
								v-if="shouldShowSavingIcon"
								:icon="['fad', 'spinner-third']"
								class="commitment-row__saving-icon fa-spin"
							/>
							<TeamingChip
								v-if="commitment.isCompleted"
								class-name="commitment-row__completed-chip"
								:text="commitment.formattedCompletedDate"
							/>
							<TeamingTextbox
								ref="commitmentText"
								class="commitment-row__content-textbox"
								:borderless="true"
								:focus="commitment.isBeingEdited"
								:is-readonly="!isBeingEdited"
								:is-valid="!isTouched || commitment.isContentValid"
								:name="`commitment-${ commitment.id }-content`"
								:value="commitmentContent"
								@input="onCommitmentTextInput"
								@enterKey="onCommitmentTextEnterKey"
								@escapeKey="onCommitmentTextEscapeKey"
							/>
						</div>
						<div
							v-if="commitment.isNewCommitment"
							class="commitment-save-btn-column"
						>
							<TeamingButton
								type="PRIMARY"
								size="SMALL"
								:disabled="isSaveDisabled"
								:leftIcon="['fas', 'check']"
								@buttonPress="onSaveNewActionItem"
							></TeamingButton>
						</div>
						<div class="commitment-row__del-btn-column">
							<TeamingButton
								type="TERTIARY"
								size="SMALL"
								:leftIcon="['far', 'trash']"
								:destructive="true"
								:disabled="isSaving"
								@buttonPress="onDeleteActionItem"
							/>
						</div>
						<div class="commitment-row__due-column">
							<CommitmentDueDatePicker
								:due="dueDate"
								:completed="completedDate"
								@click="onToggleCalendar"
							/>
						</div>
						<div
							class="commitment-row__assignee-column-container"
							@click="onAssigneePickerToggled"
						>
							<div class="commitment-row__assignee-column">
								<UserAvatar
									v-if="commitment.assignees.length"
									size="1.375rem"
									class-name="commitment-row__assignees"
									:user="commitment.assignees[ 0 ]"
								/>
								<FontAwesomeIcon
									v-else
									class="commitment-row__assignee-icon"
									:icon="['fas', 'user']"
								/>
							</div>
						</div>
					</div>
				</div>
				<div
					v-if="shouldShowContextRow"
					class="commitment-row__context-row"
				>
					<div
						v-if="shouldShowContextTeamName"
						class="commitment-row__context-cell"
					>
						<FontAwesomeIcon
							:icon="teamIcon"
							class="commitment-row__context-icon"
						/>
						<span @click="onContextRowClick">
							{{ commitment.teamName }}
						</span>
					</div>
					<div
						v-if="shouldShowContextMeetingDate"
						class="commitment-row__context-cell"
					>
						<FontAwesomeIcon
							:icon="[ 'fas', 'file-lines' ]"
							class="commitment-row__context-icon"
						/>
						<span @click="onContextRowClick">
							{{ meetingDate }}
						</span>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { DateTime } from "luxon";
import cloneDeep from "lodash/cloneDeep";

import { OutcomeDisplayLocation } from "@teaming/common-enums";

import CommitmentDueDatePicker from "./CommitmentDueDatePicker.vue";
import DebugMixin from "../../mixins/DebugMixin";
import EnumsMixin from "../../mixins/EnumsMixin";
import TeamingButton from "../buttons/TeamingButton.vue";
import TeamingCheckbox from "../TeamingCheckbox";
import TeamingChip from "../TeamingChip";
import TeamingTextbox from "../TeamingTextbox";
import UserAvatar from "../UserAvatar";

export const CommitmentRowFields = Object.freeze( {
	COMPLETION: 0,
	CONTENT: 1,
	DUE: 2,
	ASSIGNEE: 3,
} );

export default {
	name: "CommitmentRow",
	teamingPackage: "common-ui",
	mixins: [ EnumsMixin, DebugMixin, ],
	emits: [
		"assigneeMenuOpen",
		"assigneeSelect",
		"calendarPickerOpen",
		"check",
		"commitmentTextChange",
		"commitmentTextEditCancel",
		"commitmentTextEnterKey",
		"contextClick",
		"delete",
		"editText",
		"save",
	],
	components: {
		CommitmentDueDatePicker,
		TeamingButton,
		TeamingCheckbox,
		TeamingChip,
		TeamingTextbox,
		UserAvatar,
	},
	props: {
		commitment: {
			type: Object,
			required: true,
		},
		canCompleteCommitment: {
			type: Boolean,
			default: true,
		},
		isBeingEdited: {
			type: Boolean,
			default: false,
		},
		isSaving: {
			type: Boolean,
			default: false,
		},
		outcomeDisplayLocation: {
			type: String,
			default: OutcomeDisplayLocation.TEAM_SPACE,
			validator( type ) {
				return OutcomeDisplayLocation.includes( type );
			},
		},
	},
	data() {
		return {
			isTouched: false,
		};
	},
	computed: {
		commitmentContent() {
			return this.isBeingEdited ?
				this.commitment.contentBeingEdited :
				this.commitment.content;
		},
		dueDate() {
			return this.commitment.dueDate?.toDate() || undefined;
		},
		completedDate() {
			return this.commitment.completed?.toDate() || undefined;
		},
		isSaveDisabled() {
			return this.isSaving || !this.commitment.contentBeingEdited;
		},
		wrapperClasses() {
			return {
				"commitment-row__highlight-wrapper": true,
				"commitment-row__highlight-wrapper--is-editing": this.isBeingEdited,
			};
		},
		meetingDate() {
			return DateTime.fromISO( this.commitment.plannedStart, { zone: "utc", } )
				.toFormat( "DD" ); // Jul 10, 2023
		},
		selectedAssignee() {
			return this.commitment.assignments[ 0 ]?.assignedId;
		},
		shouldDisableCheckbox() {
			return !this.canCompleteCommitment;
		},
		shouldShowContextRow() {
			return this.shouldShowContextTeamName || this.shouldShowContextMeetingDate;
		},
		shouldShowContextTeamName() {
			return ( this.outcomeDisplayLocation === this.enums.OutcomeDisplayLocation.MY_ACTION_ITEMS ) &&
				!!this.commitment.teamName;
		},
		shouldShowContextMeetingDate() {
			return this.commitment.outcomeVersion === this.enums.OutcomeVersion.V2 &&
				( !!this.commitment.meetingId || ( !!this.commitment.subDocId && this.commitment.plannedStart ) );
		},
		shouldShowSavingIcon() {
			return this.isSaving;
		},
		teamIcon() {
			return this.commitment.teamType === this.enums.TeamType.ONE_ON_ONE ?
				[ "fac", "one-on-one", ] :
				[ "fas", "users", ];
		},
	},
	methods: {
		async onAssigneePickerToggled( e ) {
			await this.$nextTick();
			this.$emit(
				"assigneeMenuOpen",
				{
					commitment: cloneDeep( this.commitment ),
					event: e,
					assignedId: this.selectedAssignee,
				}
			);
		},
		onCommitmentTextClicked() {
			const { id, content, } = this.commitment;
			this.$emit( "editText", {
				id,
				content,
			} );
		},
		async onCommitmentTextInput( e ) {
			this.isTouched = true;
			const content = e.target.value;
			await this.$nextTick();
			this.$emit(
				"commitmentTextChange",
				{
					id: this.commitment.id,
					content,
					isNewCommitment: this.commitment.isNewCommitment,
				}
			);
		},
		onCommitmentTextEscapeKey() {
			const { commitment, } = this;
			this.$emit( "commitmentTextEditCancel", { commitment, } );
		},
		async onCommitmentTextEnterKey( e ) {
			e.preventDefault();
			e.stopPropagation();
			const { commitment, isSaving, } = this;
			if ( isSaving ) {
				return;
			}
			await this.$nextTick();
			this.$emit( "commitmentTextEnterKey", { commitment, } );
		},
		async onCompletionChange( e ) {
			const { isChecked, event, } = e;
			event.preventDefault();
			event.stopPropagation();
			if ( this.isSaving ) {
				return;
			}
			await this.$nextTick();
			this.$emit( "check", isChecked );
		},
		onContextRowClick() {
			const {
				teamId,
				subDocId,
				outcomeVersion,
				teamType,
			} = this.commitment;

			this.$emit( "contextClick", {
				teamId,
				subDocId,
				outcomeVersion,
				teamType,
			} );
		},
		onDeleteActionItem() {
			this.$emit( "delete", this.commitment );
		},
		onSaveNewActionItem() {
			this.$emit( "save", this.commitment );
		},
		onToggleCalendar( e ) {
			this.$emit(
				"calendarPickerOpen",
				{
					commitment: cloneDeep( this.commitment ),
					event: e,
					dateValue: this.dueDate || new Date(),
				}
			);
		},
	},
};
</script>

<style lang="scss">
.commitment-save-btn-column {
	.teamingButton {
		height: 1.5rem;
		width: 1.5rem;
		padding-left: 0.4rem;
		padding-right: 0.4rem;
		border-radius: 0.25rem;
	}
	margin-right: .25rem;
}

.commitment-row__del-btn-column {
	.teamingButton {
		height: 1.5rem;
		width: 1.5rem;
		padding-left: 0.4rem;
		padding-right: 0.4rem;
		border-radius: 0.25rem;
	}
}

.commitment-row__due-column {
	.teaming-chip {
		height: 1.5rem;
		display: flex !important; // necessary to override default chip styles
		align-items: center;
	}
}

.commitment-row__content-column {
	.teaming-textbox__input {
		width: 100%;
		background: $commitment-row-textbox-bg-color;
		padding: 0 0.25rem 0 0.25rem !important; // necessary to override selectivity of TeamingTextbox
		border-radius: .25rem;
	}

	.teaming-textbox--readonly {
		.teaming-textbox__input {
			background: transparent;
		}
	}

	.teaming-textbox--invalid {
		.teaming-textbox__input {
			border: 0.0625rem solid $commitment-row-textbox-border-color--invalid;
		}

		.teaming-textbox__validation-error-icon {
			margin-left: .15rem;
		}
	}
}
</style>

<style lang="scss" scoped>
.commitment-row {
	display: flex;

	.teamingButton {
		visibility:hidden;
	}

	&:hover {
		background-color: $commitment-row-hover-background-color;

		.teaming-textbox {
			background-color: $commitment-row-hover-background-color;
		}

		.teamingButton {
			visibility: visible;
		}
	}
}

.commitment-row__highlight-wrapper {
	border-radius: .5rem;
	outline: none;
	flex-grow: 1;
	display: flex;
	padding: 0.5rem 0 0.5rem 0;
}

.commitment-row__highlight-wrapper--is-editing {
	color: $commitment-row-highlight-color;
	background: $commitment-row-highlight-bg-color;
	border: 0.09375rem solid $commitment-row-highlight-border-color;
	box-shadow: 0rem 0rem 0rem 0.1875rem $commitment-row-highlight-box-shadow-color;

	.teamingButton {
		visibility: visible;
	}
}

.commitment-row__details-and-context-column {
	flex-grow: 1;
}

.commitment-row__details-row {
	display: flex;
}

.commitment-row__details-cell {
	display: flex;
	flex-grow: 1;
	gap: .5rem;

	@include for-size-and-smaller('sm') {
		gap: .15rem;
	}
}

.commitment-row__context-row {
	display: flex;
	padding: 0rem 0.25rem;
	gap: .75rem;
	padding-left: 2.25rem;
}

.commitment-row__context-cell {
	align-items: center;
	@include font-and-line-for(-2);
	color: $commitment-row-context-text-color;
	cursor: pointer;
}

.commitment-row__context-icon {
	color: $commitment-row-context-icon-color;
	margin-right: .25rem;
}

.commitment-row__completion-column {
	width: 1.875rem;
	display: flex;
	padding: 0.125rem 0.5rem 0 0.5rem;
	align-items: baseline;
}

.commitment-row__content-column {
	width: 100%;
	display: flex;
	align-items: center;
	padding: 0 0.25rem;
	text-align: left;
	border-radius: .25rem;
}

.commitment-row__due-column {
	display: flex;
	justify-content: center;
	align-items: baseline;
}

.commitment-row__due-chip {
	height: 1.5rem;
	display: flex !important; // necessary to override default chip styles
	align-items: baseline;
}

.commitment-row__assignee-column-container {
	display: flex;
	justify-content: center;
	align-items: baseline;
	margin-right: 0.15rem;
}

.commitment-row__assignee-column {
	border-radius: 50%;
	background: $commitment-row-assignee-bg-color;
	color: $commitment-row-assignee-font-color;
	height: 1.5rem;
	width: 1.5rem;
	border: 0.0625rem solid $commitment-row-assignee-border-color;
	box-shadow: 0 .0625rem .25rem rgba(42, 47, 69, 0.1);
}

.commitment-row__assignee-icon {
	cursor: pointer;
	width: 1.25rem;
	margin-top: 0.125rem;
}

.commitment-row__saving-icon {
	margin-right: .5rem;
	color: $commitment-row-saving-icon-primary-color;
}

.commitment-row__completed-chip {
	background-color: $commitment-row-completed-chip-background-color;
	color: $commitment-row-completed-chip-color;
	margin: 0 0.3125rem;

	&.teaming-chip {
		display: inline-block;
		white-space: nowrap;
	}
}

.commitment-row__content-textbox {
	display: inline-flex;
	text-align: left;
	@include font-and-line-for(0);
	width: 100%;
}

.commitment-row__assignees {
	.user-avatar {
		width: 1.5rem !important;
	}
}
</style>
